import { useCallback, useEffect, useState } from 'react';
import { api, saveFile, type Company, type JpkSubmission } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { date } from '../format';
import * as Icon from './Icons';
import { ErrorAlert, failureOf, type Failure } from './Alerts';

/**
 * Wysyłka JPK_V7 do urzędu (E8-S2).
 *
 * Osoba fizyczna podpisuje kwotą przychodu i wysyłka idzie od razu. Spółka dostaje plik metadanych,
 * podpisuje go podpisem kwalifikowanym albo zaufanym poza Firmeto i wgrywa z powrotem — wtedy
 * wysyłka rusza. Urząd odpowiada po kilku minutach; UPO pojawia się w historii samo.
 */
export function JpkSubmissions({ company, month, t, locale }: { company: Company; month: string; t: Dictionary; locale: Locale }) {
  const [items, setItems] = useState<JpkSubmission[] | null>(null);
  const [income, setIncome] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);
  const person = company.legalForm === 'JDG';

  const load = useCallback(async () => {
    try {
      setItems(await api.jpkSubmissions(company.id));
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  async function uploadSigned(id: string, file: File | undefined) {
    if (!file) return;
    await act(async () => api.jpkSigned(company.id, id, await file.text()));
  }

  const ofMonth = items?.filter((item) => item.period === month) ?? [];

  return (
    <div className="panel">
      <header>
        <h4>{t.jpkSend.title}</h4>
      </header>
      <p className="sm muted">{person ? t.jpkSend.personLead : t.jpkSend.companyLead}</p>
      <ErrorAlert error={error} t={t} />

      <div className="row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {person && (
          <label className="fld">
            <span>{t.jpkSend.income}</span>
            <input className="inp num" inputMode="decimal" value={income} onChange={(event) => setIncome(event.target.value)} />
          </label>
        )}
        <button
          type="button"
          className="btn"
          disabled={busy || (person && income.trim() === '')}
          onClick={() => void act(() => api.submitJpk(company.id, month, person ? income.replace(',', '.').trim() : null))}
        >
          <Icon.Upload />
          {person ? t.jpkSend.send : t.jpkSend.prepare}
        </button>
      </div>

      <h4 style={{ marginTop: 16 }}>{t.jpkSend.history}</h4>
      {items && ofMonth.length === 0 && <p className="sm muted">{t.jpkSend.none}</p>}
      {ofMonth.map((item) => (
        <div key={item.id} className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, padding: '8px 0' }}>
          <div>
            <span className={item.status === 'ACCEPTED' ? 'chip st-ok' : item.status === 'REJECTED' || item.status === 'FAILED' ? 'chip st-bad' : 'chip st-draft'}>
              <i />
              {t.jpkSend.statuses[item.status]}
            </span>{' '}
            <span className="sm muted">
              {date(item.createdAt, locale)}
              {item.referenceNumber ? ` · ${t.jpkSend.reference}: ${item.referenceNumber}` : ''}
              {item.environment === 'test' ? ` · ${t.jpkSend.test}` : ''}
            </span>
            {item.statusDescription && item.status !== 'ACCEPTED' && <div className="sm muted">{item.statusDescription}</div>}
          </div>
          <div className="row" style={{ gap: 8 }}>
            {item.status === 'AWAITING_SIGNATURE' && (
              <>
                <button type="button" className="btn sec sm" disabled={busy} onClick={() => void act(async () => saveFile(await api.jpkInitUpload(company.id, item.id)))}>
                  <Icon.Download />
                  {t.jpkSend.downloadToSign}
                </button>
                <label className="btn sec sm">
                  <Icon.Upload />
                  {t.jpkSend.uploadSigned}
                  <input type="file" accept=".xml,.xades" hidden onChange={(event) => void uploadSigned(item.id, event.target.files?.[0])} />
                </label>
              </>
            )}
            {item.hasUpo && (
              <button type="button" className="btn sec sm" disabled={busy} onClick={() => void act(async () => saveFile(await api.jpkUpo(company.id, item.id)))}>
                <Icon.Download />
                {t.jpkSend.upo}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
